import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";

export default function TermsPage() {
  return (
    <div className="min-h-screen pt-32 pb-24 px-6 md:px-12" data-testid="terms-page">
      <SEO title="Terms & Conditions" description="3T Terms and Conditions. Shipping, returns, privacy and legal information for Australian customers." />
      <div className="max-w-[900px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <p className="font-mono text-[#FFD700] text-sm tracking-[0.3em] mb-2">LEGAL</p>
          <h1 className="font-heading text-5xl md:text-7xl tracking-tight uppercase leading-[0.9]">
            TERMS & <span className="text-[#FFD700]">CONDITIONS</span>
          </h1>
          <p className="font-mono text-[#A3A3A3] text-xs mt-4">Last updated: July 2026</p>
        </motion.div>

        <div className="space-y-12 text-[#C0C0C0] text-base leading-relaxed">
          {/* 1 */}
          <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <h2 className="font-heading text-xl text-white mb-4">1. GENERAL</h2>
            <p>
              These Terms and Conditions ("Terms") govern your access to and use of the 3T website and online store (the "Site"), operated by 3T ("we", "us", "our"). By accessing or purchasing from this Site, you agree to be bound by these Terms. If you do not agree, please do not use our Site.
            </p>
          </motion.section>

          {/* 2 */}
          <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
            <h2 className="font-heading text-xl text-white mb-4">2. ELIGIBILITY</h2>
            <p>
              You must be at least 18 years of age or have the consent of a parent or legal guardian to use this Site and make purchases. By placing an order, you confirm that you meet these requirements.
            </p>
          </motion.section>

          {/* 3 */}
          <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <h2 className="font-heading text-xl text-white mb-4">3. PRODUCTS & PRICING</h2>
            <ul className="list-disc list-inside space-y-2">
              <li>All prices are displayed in Australian Dollars (AUD) and include GST where applicable.</li>
              <li>We reserve the right to modify prices at any time without prior notice.</li>
              <li>Product images are for illustrative purposes. Slight variations in colour may occur due to screen settings.</li>
              <li>We make every effort to ensure product descriptions and sizing information are accurate, but we do not warrant that they are error-free.</li>
            </ul>
          </motion.section>

          {/* 4 */}
          <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}>
            <h2 className="font-heading text-xl text-white mb-4">4. ORDERS & PAYMENT</h2>
            <ul className="list-disc list-inside space-y-2">
              <li>All orders are subject to availability and confirmation of payment.</li>
              <li>We accept payments via Stripe (credit/debit cards). Payment is processed securely through Stripe's platform.</li>
              <li>An order confirmation will be sent to the email associated with your account upon successful payment.</li>
              <li>We reserve the right to refuse or cancel any order for any reason, including suspected fraud.</li>
            </ul>
          </motion.section>

          {/* 5 */}
          <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
            <h2 className="font-heading text-xl text-white mb-4">5. SHIPPING & DELIVERY</h2>
            <ul className="list-disc list-inside space-y-2">
              <li>We currently ship within Australia only.</li>
              <li>Shipping costs are calculated at checkout based on your state/territory and the number of items ordered.</li>
              <li>Orders of 3 or more items qualify for free shipping.</li>
              <li>Estimated delivery times are provided as a guide only and are not guaranteed. Delays may occur due to circumstances beyond our control.</li>
              <li>Risk of loss and title for items pass to you upon delivery to the carrier.</li>
            </ul>
          </motion.section>

          {/* 6 */}
          <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}>
            <h2 className="font-heading text-xl text-white mb-4">6. RETURNS & EXCHANGES</h2>
            <ul className="list-disc list-inside space-y-2">
              <li>We accept returns and exchanges within 30 days of delivery, provided the item is unworn, unwashed, and in its original condition with all tags attached.</li>
              <li>To initiate a return, please contact us at hello@think3t.com.au with your order number and reason for return.</li>
              <li>Return shipping costs are the responsibility of the customer unless the item is faulty or incorrect.</li>
              <li>Refunds will be processed to the original payment method within 5–10 business days of receiving the returned item.</li>
            </ul>
          </motion.section>

          {/* 7 */}
          <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
            <h2 className="font-heading text-xl text-white mb-4">7. INTELLECTUAL PROPERTY</h2>
            <p>
              All content on this Site — including but not limited to text, graphics, logos, images, designs, and software — is the property of 3T and is protected by Australian and international copyright, trademark, and intellectual property laws. You may not reproduce, distribute, modify, or create derivative works from any content without our prior written consent.
            </p>
          </motion.section>

          {/* 8 */}
          <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45 }}>
            <h2 className="font-heading text-xl text-white mb-4">8. USER ACCOUNTS</h2>
            <ul className="list-disc list-inside space-y-2">
              <li>You are responsible for maintaining the confidentiality of your account credentials.</li>
              <li>You agree to provide accurate, current, and complete information when creating an account.</li>
              <li>We reserve the right to suspend or terminate accounts that violate these Terms or engage in fraudulent activity.</li>
            </ul>
          </motion.section>

          {/* 9 */}
          <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}>
            <h2 className="font-heading text-xl text-white mb-4">9. PRIVACY</h2>
            <p>
              Your personal information is collected, used, and stored in accordance with our Privacy Policy and in compliance with the Australian Privacy Act 1988. We do not sell or share your personal data with third parties except as necessary to process orders and provide our services.
            </p>
          </motion.section>

          {/* 10 */}
          <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55 }}>
            <h2 className="font-heading text-xl text-white mb-4">10. LIMITATION OF LIABILITY</h2>
            <p>
              To the fullest extent permitted by law, 3T shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of or related to your use of the Site or purchase of products. Our total liability shall not exceed the amount paid by you for the specific product giving rise to the claim.
            </p>
          </motion.section>

          {/* 11 */}
          <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}>
            <h2 className="font-heading text-xl text-white mb-4">11. AUSTRALIAN CONSUMER LAW</h2>
            <p>
              Nothing in these Terms excludes, restricts, or modifies any consumer rights under the Australian Consumer Law (Schedule 2 of the Competition and Consumer Act 2010). If a product has a major failure, you are entitled to a replacement or refund. If a product has a minor failure, we may choose to repair, replace, or refund.
            </p>
          </motion.section>

          {/* 12 */}
          <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.65 }}>
            <h2 className="font-heading text-xl text-white mb-4">12. GOVERNING LAW</h2>
            <p>
              These Terms are governed by and construed in accordance with the laws of Australia. Any disputes arising from these Terms or your use of the Site shall be subject to the exclusive jurisdiction of the courts of Australia.
            </p>
          </motion.section>

          {/* 13 */}
          <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }}>
            <h2 className="font-heading text-xl text-white mb-4">13. CHANGES TO THESE TERMS</h2>
            <p>
              We reserve the right to update or modify these Terms at any time. Changes will be effective immediately upon posting on the Site. Your continued use of the Site after any changes constitutes acceptance of the updated Terms.
            </p>
          </motion.section>

          {/* 14 */}
          <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.75 }}>
            <h2 className="font-heading text-xl text-white mb-4">14. CONTACT</h2>
            <p>
              If you have any questions about these Terms, please contact us at:
            </p>
            <div className="mt-4 border-l-4 border-[#FFD700] pl-6 py-2">
              <p className="font-heading text-white">3T</p>
              <a href="mailto:hello@think3t.com.au" className="font-mono text-sm text-[#FFD700] hover:underline">hello@think3t.com.au</a>
            </div>
          </motion.section>

          <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8 }}>
            <p className="text-[#A3A3A3] text-sm">
              See also: <Link to="/privacy" className="text-[#FFD700] hover:underline">Privacy Policy</Link> · <Link to="/shipping" className="text-[#FFD700] hover:underline">Shipping</Link> · <Link to="/returns" className="text-[#FFD700] hover:underline">Returns & Exchanges</Link> · <Link to="/refund-policy" className="text-[#FFD700] hover:underline">Refund Policy</Link>
            </p>
          </motion.section>
        </div>
      </div>
    </div>
  );
}
